/**
 * Notice when a newer build is live, so an open tab doesn't keep running old
 * code (2026-09-19).
 *
 * A single-page app never reloads itself: a phone tab opened before a deploy
 * ran the OLD dictation code a day later, and its tester reported bugs that
 * had already been fixed. Each build's entry script has a content hash in its
 * name (`/assets/index-<hash>.js`), so comparing the running page's with the
 * one the server now serves says whether a newer build exists.
 *
 * It only offers a reload — never forces one, which could lose an unsaved
 * note.
 */

/** How often to look again while the page is visible. */
export const VERSION_CHECK_MS = 10 * 60_000;

/** The built entry script named in an HTML page, or null (dev server, etc.). */
export function builtScript(html: string): string | null {
  return /\/assets\/index-[\w-]+\.js/.exec(html)?.[0] ?? null;
}

/** True only when both builds are known and they differ. */
export function isNewerBuild(running: string | null, served: string | null): boolean {
  return !!running && !!served && running !== served;
}

/**
 * Check whenever the page becomes visible again, and every VERSION_CHECK_MS
 * while it stays visible — not at load, when the page is already the newest. Calls `onNewer` once, then stops —
 * the bound: no more checks after a newer build is found, and none while the
 * tab is hidden. Returns a function that stops watching.
 */
export function watchForNewVersion(
  onNewer: () => void,
  opts: { running?: string | null; fetchHtml?: () => Promise<string> } = {},
): () => void {
  const running = opts.running !== undefined
    ? opts.running
    : builtScript(document.documentElement.outerHTML);
  if (!running) return () => {};

  const fetchHtml = opts.fetchHtml
    ?? (async () => (await fetch('/', { cache: 'no-store' })).text());
  let done = false;

  const check = async (): Promise<void> => {
    if (done || document.visibilityState === 'hidden') return;
    try {
      if (isNewerBuild(running, builtScript(await fetchHtml()))) {
        done = true;
        stop();
        onNewer();
      }
    } catch { /* offline or a blip: try again next time */ }
  };

  const onVisible = (): void => { if (document.visibilityState === 'visible') void check(); };
  const timer = setInterval(() => { void check(); }, VERSION_CHECK_MS);
  document.addEventListener('visibilitychange', onVisible);
  const stop = (): void => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
  return stop;
}

/** The "new version" bar. Reloading is the person's choice. */
export function showUpdateBar(): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'update-bar';
  bar.setAttribute('role', 'status');
  bar.innerHTML = `<span>A new version of Design Studio HQ is available.</span>
    <button type="button" class="btn-sm" data-action="reload-app">Reload</button>
    <button type="button" class="btn-link" data-action="dismiss-update" aria-label="Dismiss">Later</button>`;
  bar.querySelectorAll('[data-action="reload-app"]').forEach((b) => b.addEventListener('click', () => location.reload()));
  bar.querySelectorAll('[data-action="dismiss-update"]').forEach((b) => b.addEventListener('click', () => bar.remove()));
  document.body.append(bar);
  return bar;
}
